import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../common/hooks';
import { getCMSConfig, updateCMS } from '../../../../features/adviceAdmin';
import { fetchCMSdata, updateGlobalLoaderState } from '../../../../features/global/globalSlice';

type CMSResponseType = {
    cms_config_data: Record<string, Array<{ locator: string; content: string }>>;
    success: boolean;
};

export function useCMSConfig() {
    const [data, setData] = useState<CMSResponseType | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const dispatch = useAppDispatch();

    const authTenant = useAppSelector((s: any) => s?.auth?.user?.tenant);
    const authLanguage = useAppSelector((s: any) => s?.auth?.user?.language);

    const fetchCMSConfigurations = useCallback(
        async (params?: { tenant?: string; language?: string }) => {
            setLoading(true);
            setError(null);
            try {
                const effectiveParams = params || { tenant: authTenant, language: authLanguage };
                if (!effectiveParams?.tenant) {
                    setLoading(false);
                    return null;
                }
                dispatch(updateGlobalLoaderState(true));
                const action: any = await dispatch(getCMSConfig(effectiveParams as any));
                let payload = action?.payload;
                if (payload && payload.data) payload = payload.data;
                if (payload) setData(payload as CMSResponseType);
                return payload as CMSResponseType | null;
            } catch (err: any) {
                setError(err?.message || String(err));
                return null;
            } finally {
                setLoading(false);
                dispatch(updateGlobalLoaderState(false));
            }
        },
        [dispatch, authTenant, authLanguage]
    );

    const pages = useMemo(() => {
        if (!data?.cms_config_data) return [];
        return Object.keys(data.cms_config_data);
    }, [data]);

    const locatorsForPage = useCallback(
        (page: string) => {
            if (!data?.cms_config_data) return [];
            const arr = data.cms_config_data[page] || [];
            return arr.map((it) => ({ value: it.locator, label: it.locator }));
        },
        [data]
    );

    const contentForLocator = useCallback(
        (page: string, locator: string) => {
            if (!data?.cms_config_data) return undefined;
            const arr = data.cms_config_data[page] || [];
            const found = arr.find((it) => it.locator === locator);
            return found?.content;
        },
        [data]
    );

    const updateCMSConfigs = useCallback(
        async (payload: { tenants: string | string[]; locator: string; content: string; language: string }) => {
            setLoading(true);
            setError(null);
            dispatch(updateGlobalLoaderState(true));
            try {
                const action: any = await dispatch(updateCMS(payload as any));
                const resp = action?.payload;
                // refresh the CMS configurations for the tenant/language after successful update
                if (resp) {
                    const tenantForRefresh = Array.isArray(payload.tenants) ? payload.tenants[0] : payload.tenants;
                    const isCurrentTenantSelected = Array.isArray(payload.tenants)
                        ? payload.tenants.includes(authTenant)
                        : payload.tenants === authTenant;
                    if (isCurrentTenantSelected) {
                        dispatch(fetchCMSdata({ language: payload.language, tenantId: authTenant }));
                    }
                    await fetchCMSConfigurations({ tenant: tenantForRefresh, language: payload.language });
                }
                return resp;
            } catch (err: any) {
                setError(err?.message || String(err));
                return null;
            } finally {
                setLoading(false);
                dispatch(updateGlobalLoaderState(false));
            }
        },
        [dispatch, fetchCMSConfigurations, authTenant]
    );

    return {
        pages,
        locatorsForPage,
        contentForLocator,
        loading,
        error,
        refresh: fetchCMSConfigurations,
        updateCMSConfigs
    };
}
